import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, X, Edit2, Key, UserX, UserCheck, Shield, Lock } from 'lucide-react';
import { LoadingButton } from '../components/QuickWins';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: '',
    is_admin: false,
    permissions: {
      clients: true,
      ledger: true,
      cash_pool: false,
      cycles: true,
      expenses: false,
      reports: true,
      company_pnl: false,
      approvals: false,
      users: false,
      audit_logs: false
    }
  });
  
  const [newPassword, setNewPassword] = useState('');
  const [changePasswordData, setChangePasswordData] = useState({
    current_password: '',
    new_password: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Ensure non-admin users cannot have approvals permission
      const finalPermissions = { ...formData.permissions };
      if (!formData.is_admin) {
        finalPermissions.approvals = false;
      }
      
      await axiosInstance.post('/users', {
        ...formData,
        permissions: finalPermissions
      });
      toast.success(`User "${formData.username}" created successfully`);
      setShowCreateModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Ensure non-admin users cannot have approvals permission
      const finalPermissions = { ...formData.permissions };
      if (!formData.is_admin) {
        finalPermissions.approvals = false;
      }
      
      await axiosInstance.put(`/users/${selectedUser.id}`, {
        name: formData.name,
        is_admin: formData.is_admin,
        permissions: finalPermissions,
        status: formData.status
      });
      toast.success(`User "${selectedUser.username}" updated successfully`);
      setShowEditModal(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axiosInstance.put(`/users/${selectedUser.id}/reset-password`, {
        new_password: newPassword
      });
      toast.success(`Password reset for "${selectedUser.username}"`);
      setShowResetPasswordModal(false);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangeOwnPassword = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axiosInstance.put('/auth/change-password', changePasswordData);
      toast.success('Your password has been changed');
      setShowChangePasswordModal(false);
      setChangePasswordData({ current_password: '', new_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeactivateUser = async (userId, username) => {
    if (!window.confirm(`Deactivate user "${username}"? They will not be able to login.`)) return;
    
    try {
      await axiosInstance.delete(`/users/${userId}`);
      toast.success(`User "${username}" deactivated`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate user');
    }
  };

  const handleReactivateUser = async (userId, username) => {
    try {
      await axiosInstance.put(`/users/${userId}`, { status: 'active' });
      toast.success(`User "${username}" reactivated`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate user');
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      is_admin: user.is_admin,
      permissions: user.permissions || {},
      status: user.status
    });
    setShowEditModal(true);
  };

  const openResetPasswordModal = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowResetPasswordModal(true);
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      name: '',
      is_admin: false,
      permissions: {
        clients: true,
        ledger: true,
        cash_pool: false,
        cycles: true,
        expenses: false,
        reports: true,
        company_pnl: false,
        approvals: false,
        users: false,
        audit_logs: false
      }
    });
  };

  const permissionLabels = {
    clients: 'Clients',
    ledger: 'Ledger',
    cash_pool: 'Cash Pool',
    cycles: 'Cycles',
    expenses: 'Expenses',
    reports: 'Reports',
    company_pnl: 'Company P&L',
    approvals: 'Approvals',
    users: 'User Management',
    audit_logs: 'Audit Logs'
  };
  
  // Permissions that can only be granted to admin
  const adminOnlyPermissions = ['approvals'];

  if (loading) return <div className="p-8">Loading...</div>;

  return (
    <div className="p-8" data-testid="users-page">
      <div className="cb-page-header">
        <div className="flex justify-between items-end" style={{ gap: 'var(--cb-space-4)' }}>
          <div>
            <p className="cb-eyebrow">Access Control</p>
            <h1 className="cb-h1">User Management</h1>
            <p className="cb-section-sub" style={{ marginTop: 'var(--cb-space-2)' }}>Manage system users and permissions</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowChangePasswordModal(true)}
              data-testid="change-own-password-btn"
              className="cb-btn-secondary"
            >
              <Key className="w-4 h-4" />
              Change My Password
            </button>
            <button
              onClick={() => { resetForm(); setShowCreateModal(true); }}
              data-testid="create-user-btn"
              className="cb-btn-primary"
            >
              <Plus className="w-4 h-4" />
              Add User
            </button>
          </div>
        </div>
        <hr className="cb-divider" />
      </div>

      {/* Users Table */}
      <div className="cb-card" style={{ padding: 0, overflowX: 'auto' }}>
        <table data-testid="users-table" className="cb-table" style={{ minWidth: '100%' }}>
          <thead>
            <tr>
              <th>Username</th>
              <th>Name</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td style={{ fontWeight: 500 }}>{user.username || user.email || '-'}</td>
                <td>{user.name}</td>
                <td>
                  {user.is_admin ? (
                    <span className="cb-pill cb-pill--ink" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Shield className="w-3 h-3" />
                      Admin
                    </span>
                  ) : user.role === 'admin' ? (
                    <span className="cb-pill cb-pill--ink" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Shield className="w-3 h-3" />
                      Admin (legacy)
                    </span>
                  ) : (
                    <span className="cb-pill">User</span>
                  )}
                </td>
                <td>
                  <span className={`cb-pill ${
                    user.status === 'active' ? 'cb-pill--positive' :
                    user.status === 'locked' ? 'cb-pill--negative' :
                    user.status === 'inactive' ? 'cb-pill--negative' :
                    ''
                  }`}>
                    {user.status || 'unknown'}
                  </span>
                </td>
                <td className="cb-muted cb-num" style={{ fontSize: '0.8125rem' }}>
                  {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
                </td>
                <td style={{ display: 'flex', gap: 'var(--cb-space-2)', alignItems: 'center' }}>
                  <button
                    onClick={() => openEditModal(user)}
                    data-testid={`edit-user-${user.username || user.id}`}
                    title="Edit User"
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--cb-ink-soft)', padding: '4px' }}
                  >
                    <Edit2 className="w-4 h-4 inline" />
                  </button>
                  <button
                    onClick={() => openResetPasswordModal(user)}
                    data-testid={`reset-password-${user.username}`}
                    title="Reset Password"
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--cb-ink-soft)', padding: '4px' }}
                  >
                    <Key className="w-4 h-4 inline" />
                  </button>
                  {user.id !== currentUser.id && (
                    user.status === 'active' ? (
                      <button
                        onClick={() => handleDeactivateUser(user.id, user.username)}
                        data-testid={`deactivate-user-${user.username}`}
                        title="Deactivate User"
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--cb-negative)', padding: '4px' }}
                      >
                        <UserX className="w-4 h-4 inline" />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleReactivateUser(user.id, user.username)}
                        data-testid={`reactivate-user-${user.username}`}
                        title="Reactivate User"
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--cb-positive)', padding: '4px' }}
                      >
                        <UserCheck className="w-4 h-4 inline" />
                      </button>
                    )
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(11,11,10,0.55)' }}>
          <div className="cb-card w-full max-w-lg max-h-[90vh] overflow-y-auto" style={{ padding: 'var(--cb-space-6)' }}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="cb-section-title">Create New User</h2>
              <button onClick={() => setShowCreateModal(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--cb-ink-soft)' }}>
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="form-label">Username *</label>
                <input
                  type="text"
                  data-testid="create-user-username"
                  className="form-input"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                />
              </div>
              
              <div>
                <label className="form-label">Name *</label>
                <input
                  type="text"
                  data-testid="create-user-name"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              
              <div>
                <label className="form-label">Password *</label>
                <input
                  type="password"
                  data-testid="create-user-password"
                  className="form-input"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Min 8 characters, 1 uppercase letter, 1 number
                </p>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_admin"
                  data-testid="create-user-is-admin"
                  checked={formData.is_admin}
                  onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                />
                <label htmlFor="is_admin" className="text-sm font-medium">
                  Admin (Full Access)
                </label>
              </div>
              
              {!formData.is_admin && (
                <div>
                  <label className="form-label">Permissions</label>
                  <div className="grid grid-cols-2 gap-2" style={{ background: 'var(--cb-bone)', padding: 'var(--cb-space-3)', borderRadius: 'var(--cb-radius)', border: '1px solid var(--cb-line)' }}>
                    {Object.entries(permissionLabels).map(([key, label]) => {
                      const isAdminOnly = adminOnlyPermissions.includes(key);
                      return (
                        <div key={key} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`perm_${key}`}
                            data-testid={`create-user-perm-${key}`}
                            checked={formData.permissions[key] || false}
                            onChange={(e) => setFormData({
                              ...formData,
                              permissions: { ...formData.permissions, [key]: e.target.checked }
                            })}
                            disabled={isAdminOnly}
                          />
                          <label htmlFor={`perm_${key}`} className={`text-sm ${isAdminOnly ? 'cb-muted' : ''}`} style={{ color: isAdminOnly ? 'var(--cb-ink-soft)' : 'var(--cb-ink)' }}>
                            {label}
                            {isAdminOnly && (
                              <span className="cb-pill" style={{ marginLeft: 'var(--cb-space-1)' }}>
                                <Lock className="w-3 h-3 inline mr-0.5" />
                                Admin Only
                              </span>
                            )}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                  <p className="cb-muted" style={{ fontSize: '0.75rem', marginTop: 'var(--cb-space-2)' }}>
                    Note: Approvals permission is restricted to admin users only
                  </p>
                </div>
              )}
              
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowCreateModal(false)} className="cb-btn-secondary">
                  Cancel
                </button>
                <LoadingButton type="submit" loading={isSubmitting} data-testid="create-user-submit" className="cb-btn-primary">
                  Create User
                </LoadingButton>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(11,11,10,0.55)' }}>
          <div className="cb-card w-full max-w-lg max-h-[90vh] overflow-y-auto" style={{ padding: 'var(--cb-space-6)' }}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="cb-section-title">Edit User: {selectedUser.username}</h2>
              <button onClick={() => setShowEditModal(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--cb-ink-soft)' }}>
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div>
                <label className="form-label">Name *</label>
                <input
                  type="text"
                  data-testid="edit-user-name"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit_is_admin"
                  data-testid="edit-user-is-admin"
                  checked={formData.is_admin}
                  onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                />
                <label htmlFor="edit_is_admin" className="text-sm font-medium">
                  Admin (Full Access)
                </label>
              </div>
              
              {!formData.is_admin && (
                <div>
                  <label className="form-label">Permissions</label>
                  <div className="grid grid-cols-2 gap-2" style={{ background: 'var(--cb-bone)', padding: 'var(--cb-space-3)', borderRadius: 'var(--cb-radius)', border: '1px solid var(--cb-line)' }}>
                    {Object.entries(permissionLabels).map(([key, label]) => {
                      const isAdminOnly = adminOnlyPermissions.includes(key);
                      return (
                        <div key={key} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`edit_perm_${key}`}
                            data-testid={`edit-user-perm-${key}`}
                            checked={formData.permissions[key] || false}
                            onChange={(e) => setFormData({
                              ...formData,
                              permissions: { ...formData.permissions, [key]: e.target.checked }
                            })}
                            disabled={isAdminOnly}
                          />
                          <label htmlFor={`edit_perm_${key}`} className="text-sm" style={{ color: isAdminOnly ? 'var(--cb-ink-soft)' : 'var(--cb-ink)' }}>
                            {label}
                            {isAdminOnly && (
                              <span className="cb-pill" style={{ marginLeft: 'var(--cb-space-1)' }}>
                                <Lock className="w-3 h-3 inline mr-0.5" />
                                Admin Only
                              </span>
                            )}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                  <p className="cb-muted" style={{ fontSize: '0.75rem', marginTop: 'var(--cb-space-2)' }}>
                    Note: Approvals permission is restricted to admin users only
                  </p>
                </div>
              )}
              
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowEditModal(false)} className="cb-btn-secondary">
                  Cancel
                </button>
                <LoadingButton type="submit" loading={isSubmitting} data-testid="edit-user-submit" className="cb-btn-primary">
                  Save Changes
                </LoadingButton>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && selectedUser && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(11,11,10,0.55)' }}>
          <div className="cb-card w-full max-w-md" style={{ padding: 'var(--cb-space-6)' }}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="cb-section-title">Reset Password</h2>
              <button onClick={() => setShowResetPasswordModal(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--cb-ink-soft)' }}>
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="cb-muted mb-4">
              Reset password for user: <span style={{ color: 'var(--cb-ink)', fontWeight: 500 }}>{selectedUser.username}</span>
            </p>
            
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="form-label">New Password *</label>
                <input
                  type="password"
                  data-testid="reset-password-input"
                  className="form-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
                <p className="cb-muted" style={{ fontSize: '0.75rem', marginTop: 'var(--cb-space-1)' }}>
                  Min 8 characters, 1 uppercase letter, 1 number
                </p>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowResetPasswordModal(false)} className="cb-btn-secondary">
                  Cancel
                </button>
                <button type="submit" data-testid="reset-password-submit" className="cb-btn-primary">
                  Reset Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Own Password Modal */}
      {showChangePasswordModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(11,11,10,0.55)' }}>
          <div className="cb-card w-full max-w-md" style={{ padding: 'var(--cb-space-6)' }}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="cb-section-title">Change Your Password</h2>
              <button onClick={() => setShowChangePasswordModal(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--cb-ink-soft)' }}>
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleChangeOwnPassword} className="space-y-4">
              <div>
                <label className="form-label">Current Password *</label>
                <input
                  type="password"
                  data-testid="change-current-password"
                  className="form-input"
                  value={changePasswordData.current_password}
                  onChange={(e) => setChangePasswordData({ ...changePasswordData, current_password: e.target.value })}
                  required
                />
              </div>
              
              <div>
                <label className="form-label">New Password *</label>
                <input
                  type="password"
                  data-testid="change-new-password"
                  className="form-input"
                  value={changePasswordData.new_password}
                  onChange={(e) => setChangePasswordData({ ...changePasswordData, new_password: e.target.value })}
                  required
                />
                <p className="cb-muted" style={{ fontSize: '0.75rem', marginTop: 'var(--cb-space-1)' }}>
                  Min 8 characters, 1 uppercase letter, 1 number
                </p>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowChangePasswordModal(false)} className="cb-btn-secondary">
                  Cancel
                </button>
                <button type="submit" data-testid="change-password-submit" className="cb-btn-primary">
                  Change Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
